import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthProvider";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { OutreachApprovalFooterText } from "./OutreachApprovalFooterText";

type Props = {
	/** What auto-approve would have saved, already translated. */
	text: string;
	requestId: string;
};

/** The page that holds the department's auto-approve rules: department configuration, Staff App tab. */
const settingsPathOf = (departmentId: string): string => `/${departmentId}/settings/department-configuration?tab=staff-app`;

/**
 * What auto-approve would save, and the way to turn it on. That settings page is support-only, so support gets a
 * button to it and everyone else is told who can turn it on.
 */
export const OutreachAutoApproveFooter: FC<Props> = ({ text, requestId }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const { userInfo } = useAuth();
	const { chosenDepartment } = useHospitalData();
	if (!userInfo.support) return <OutreachApprovalFooterText>{`${text} ${t("request.outreach.approval.ask_support")}`}</OutreachApprovalFooterText>;
	return (
		<>
			<OutreachApprovalFooterText>{text}</OutreachApprovalFooterText>
			<SystemButton
				data-testid="outreach-approval-auto"
				buttonVariant="borderless"
				size="compact"
				noFullWidthMobile
				onClick={() => {
					track("outreach_auto_approve_settings_opened", { request_id: requestId });
					navigate(settingsPathOf(chosenDepartment.department_id));
				}}
			>
				{t("request.outreach.approval.turn_on")}
			</SystemButton>
		</>
	);
};
